import React, { useState, useEffect, useRef } from 'react';
import { MapPin, ChevronDown, ChevronUp, Linkedin } from 'lucide-react';
import Logo from './Logo';
import { linkedinCompanyUrl } from '../lib/linkedin';

const StartupCard = ({ startup, active, onClick }) => {
  const isYC = startup.yc;
  const ycLabel = isYC ? startup.badge.replace(/^Y\s*/, '') : null;

  return (
    <button
      onClick={() => onClick(startup.id)}
      className={`group flex-shrink-0 w-[320px] text-left bg-white rounded-2xl shadow-md border transition-all snap-start overflow-hidden ${
        active
          ? 'border-slate-900 ring-2 ring-slate-900/10 -translate-y-1'
          : 'border-slate-200 hover:border-slate-300 hover:-translate-y-0.5'
      }`}
    >
      <div className="p-3.5">
        <div className="flex items-center gap-2.5">
          <Logo startup={startup} size={40} />
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <span className="font-semibold text-slate-900 text-[15px] truncate">
              {startup.name}
            </span>
            {isYC && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-orange-500 text-white text-[10px] font-bold flex-shrink-0">
                Y
              </span>
            )}
            {isYC && ycLabel && (
              <span className="text-[11px] text-slate-500 font-medium">{ycLabel}</span>
            )}
          </div>
        </div>

        <p className="mt-2.5 text-[12.5px] text-slate-600 leading-snug line-clamp-2 min-h-[34px]">
          {startup.description}
        </p>

        <div className="mt-2.5">
          <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
            {startup.sector}
          </span>
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-1 text-[11.5px] text-slate-500">
          <span className="flex items-center gap-1 min-w-0">
            <MapPin size={12} className="flex-shrink-0 text-rose-500" fill="currentColor" />
            <span className="truncate">{startup.address}</span>
          </span>
          {linkedinCompanyUrl(startup) ? (
            <span
              role="link"
              tabIndex={0}
              data-testid="card-linkedin-link"
              title="View company on LinkedIn"
              onClick={(e) => { e.stopPropagation(); window.open(linkedinCompanyUrl(startup), '_blank', 'noopener'); }}
              className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md text-[#0A66C2] hover:bg-[#0A66C2]/10 transition-colors"
            >
              <Linkedin size={14} />
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
};

const CardsCarousel = ({ startups, activeId, onSelect }) => {
  const scrollRef = useRef(null);
  const cardRefs = useRef({});
  const [collapsed, setCollapsed] = useState(false);

  const shown = startups;

  useEffect(() => {
    if (activeId && cardRefs.current[activeId] && scrollRef.current) {
      const card = cardRefs.current[activeId];
      const container = scrollRef.current;
      const cardLeft = card.offsetLeft;
      const targetScroll = cardLeft - container.offsetWidth / 2 + card.offsetWidth / 2;
      container.scrollTo({ left: targetScroll, behavior: 'smooth' });
    }
  }, [activeId]);

  return (
    <div className="hidden sm:block absolute bottom-6 left-0 right-0 z-[800] pointer-events-none">
      {/* Mobile collapse toggle */}
      <div className="flex justify-center mb-2 sm:hidden">
        <button
          onClick={() => setCollapsed((v) => !v)}
          data-testid="cards-collapse-toggle"
          className="pointer-events-auto inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-md border border-slate-200/60 px-3.5 py-1.5 rounded-full text-[12px] font-medium text-slate-700 shadow-md"
        >
          <span className="font-semibold text-slate-900">{startups.length}</span> startups
          {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      <div className={`${collapsed ? 'hidden' : ''} sm:block`} data-testid="cards-carousel-body">
        <div
          ref={scrollRef}
          className="flex gap-3 px-4 overflow-x-auto snap-x snap-mandatory pointer-events-auto scrollbar-hide pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {shown.map((s) => (
            <div key={s.id} ref={(el) => (cardRefs.current[s.id] = el)}>
              <StartupCard
                startup={s}
                active={activeId === s.id}
                onClick={onSelect}
              />
            </div>
          ))}
          {!startups.length && (
            <div className="flex-shrink-0 w-full text-center text-[13px] text-slate-500 bg-white rounded-2xl border border-slate-200 py-4 shadow-md">
              No startups match your filters.
            </div>
          )}
        </div>
        <div className="text-center mt-2 text-[12px] text-slate-600 font-medium drop-shadow-sm">
          Tap a pin or card to see details.
        </div>
        <div
          data-testid="silicon-florist-credit"
          className="pointer-events-auto mx-auto mt-1.5 max-w-2xl px-4 text-center text-[11px] leading-snug text-slate-500 drop-shadow-sm"
        >
          This map is made possible thanks to the support and hard work of{' '}
          <a
            href="https://siliconflorist.com"
            target="_blank"
            rel="noreferrer"
            data-testid="silicon-florist-link"
            className="font-semibold text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-emerald-700 hover:decoration-emerald-500"
          >
            Silicon Florist
          </a>{' '}
          — all data is sourced from their reporting — and its founder, Rick Turoczy.
        </div>
      </div>
    </div>
  );
};

export default CardsCarousel;
